import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Search,
  Download,
  Plus,
  ArrowUp,
  ArrowDown,
  ChevronsUpDown,
  Loader2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

const ALL = "__all__";

export default function DataTable({
  columns,
  rows = [],
  loading,
  total = 0,
  page = 1,
  pageSize = 25,
  onPage,
  sortBy,
  sortDir,
  onSort,
  search = "",
  onSearch,
  filterFields = [],
  filterValues = {},
  onFilter,
  onExport,
  onNew,
  newLabel = "Nuovo",
  canWrite,
  renderActions,
  rowClassName,
  emptyLabel = "Nessun risultato",
  testid = "datatable",
  selectable = false,
  selectedIds = [],
  onToggleRow,
  onToggleAllPage,
  leftToolbar,
}) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const allPageSelected = rows.length > 0 && rows.every((r) => selectedIds.includes(r.id));

  return (
    <div className="bg-white border border-slate-200 rounded-md flex flex-col" data-testid={testid}>
      {/* Toolbar */}
      <div className="p-3 border-b border-slate-200 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => onSearch?.(e.target.value)}
            placeholder="Cerca..."
            className="pl-8 h-9"
            data-testid={`${testid}-search`}
          />
        </div>
        {filterFields.map((f) => (
          <Select
            key={f.name}
            value={filterValues[f.name] ?? ALL}
            onValueChange={(v) => onFilter?.(f.name, v === ALL ? "" : v)}
          >
            <SelectTrigger className="h-9 w-auto min-w-[130px]" data-testid={`${testid}-filter-${f.name}`}>
              <SelectValue placeholder={f.label} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{f.label}: tutti</SelectItem>
              {f.options.map((o) => (
                <SelectItem key={String(o.value)} value={String(o.value)}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ))}
        <div className="flex items-center gap-2 ml-auto">
          {leftToolbar}
          {onExport && (
            <Button variant="outline" size="sm" onClick={onExport} className="h-9" data-testid={`${testid}-export`}>
              <Download className="h-4 w-4 mr-1.5" />
              Excel
            </Button>
          )}
          {canWrite && onNew && (
            <Button size="sm" onClick={onNew} className="h-9" data-testid={`${testid}-new`}>
              <Plus className="h-4 w-4 mr-1.5" />
              {newLabel}
            </Button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-slate-50 z-10">
            <tr className="border-b border-slate-200">
              {selectable && (
                <th className="px-3 py-2.5 w-1">
                  <Checkbox
                    checked={allPageSelected}
                    onCheckedChange={(c) => onToggleAllPage?.(rows, !!c)}
                    data-testid={`${testid}-select-all`}
                  />
                </th>
              )}
              {columns.map((c) => {
                const active = sortBy === (c.sortKey || c.key);
                return (
                  <th
                    key={c.key}
                    className={`px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap ${
                      c.align === "right" ? "text-right" : "text-left"
                    } ${c.sortable !== false && onSort ? "cursor-pointer select-none hover:text-slate-800" : ""}`}
                    onClick={() =>
                      c.sortable !== false && onSort && onSort(c.sortKey || c.key)
                    }
                  >
                    <span className="inline-flex items-center gap-1">
                      {c.label}
                      {c.sortable !== false && onSort && (
                        active ? (
                          sortDir === "asc" ? (
                            <ArrowUp className="h-3 w-3" />
                          ) : (
                            <ArrowDown className="h-3 w-3" />
                          )
                        ) : (
                          <ChevronsUpDown className="h-3 w-3 opacity-30" />
                        )
                      )}
                    </span>
                  </th>
                );
              })}
              {renderActions && <th className="px-3 py-2.5 w-1" />}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length + (renderActions ? 1 : 0) + (selectable ? 1 : 0)} className="py-16 text-center text-slate-400">
                  <Loader2 className="h-5 w-5 animate-spin inline" />
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + (renderActions ? 1 : 0) + (selectable ? 1 : 0)}
                  className="py-16 text-center text-slate-400 text-sm"
                  data-testid={`${testid}-empty`}
                >
                  {emptyLabel}
                </td>
              </tr>
            ) : (
              rows.map((row, ri) => (
                <tr
                  key={row.id || ri}
                  className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${
                    rowClassName ? rowClassName(row) : ""
                  }`}
                  data-testid={`${testid}-row`}
                >
                  {selectable && (
                    <td className="px-3 py-2">
                      <Checkbox
                        checked={selectedIds.includes(row.id)}
                        onCheckedChange={() => onToggleRow?.(row.id)}
                        data-testid={`${testid}-select-${row.id}`}
                      />
                    </td>
                  )}
                  {columns.map((c) => (
                    <td
                      key={c.key}
                      className={`px-3 py-2 text-slate-700 ${c.align === "right" ? "text-right tabular-nums" : "text-left"} ${c.nowrap ? "whitespace-nowrap" : ""}`}
                    >
                      {c.render ? c.render(row) : row[c.key] ?? "—"}
                    </td>
                  ))}
                  {renderActions && (
                    <td className="px-3 py-2 text-right whitespace-nowrap">{renderActions(row)}</td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="p-3 border-t border-slate-200 flex items-center justify-between text-xs text-slate-500">
        <span data-testid={`${testid}-count`}>
          {from}–{to} di {total}
        </span>
        {onPage && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="h-8"
              disabled={page <= 1}
              onClick={() => onPage(page - 1)}
              data-testid={`${testid}-prev`}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span>
              Pagina {page} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              className="h-8"
              disabled={page >= totalPages}
              onClick={() => onPage(page + 1)}
              data-testid={`${testid}-next`}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
